import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { brandQuery, useBrand } from "../brandContext";

type CopyRow = {
  host: string;
  source: string;
  count: number;
  max_score: number;
  first_seen?: string;
};

type SortKey = "max_score" | "count" | "host" | "first_seen";

export function CopiesPage() {
  const { activeId } = useBrand();
  const navigate = useNavigate();
  const [items, setItems] = useState<CopyRow[]>([]);
  const [high, setHigh] = useState(0);
  const [medium, setMedium] = useState(0);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({
    key: "max_score",
    dir: -1,
  });

  useEffect(() => {
    void fetch(`/api/copies${brandQuery(activeId)}`)
      .then((r) => r.json())
      .then((d) => {
        setItems(d.items || []);
        setHigh(d.high || 0);
        setMedium(d.medium || 0);
      });
  }, [activeId]);

  const isNewHost = (r: CopyRow) =>
    !!r.first_seen && Date.now() - Date.parse(r.first_seen) < 24 * 3600_000;

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const k = sort.key;
      const va = a[k] ?? "";
      const vb = b[k] ?? "";
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * sort.dir;
      }
      return String(va).localeCompare(String(vb)) * sort.dir;
    });
    return arr;
  }, [items, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: -1 },
    );
  }

  function arrow(key: SortKey) {
    return sort.key === key ? (sort.dir === -1 ? " ↓" : " ↑") : "";
  }

  function drill(r: CopyRow) {
    if (!r.host) return;
    navigate(`/mentions?host=${encodeURIComponent(r.host)}`);
  }

  return (
    <>
      <div className="page-head">
        <h2>Копии и совпадения</h2>
        <div className="metrics" style={{ margin: 0 }}>
          <div className="metric">
            <div className="n">{high}</div>
            <div className="l">high</div>
          </div>
          <div className="metric">
            <div className="n">{medium}</div>
            <div className="l">medium</div>
          </div>
        </div>
      </div>
      <div className="panel">
        <h3>Где и сколько — клик по строке открывает находки хоста</h3>
        <table className="table table-sortable">
          <thead>
            <tr>
              <th onClick={() => toggleSort("host")}>Где{arrow("host")}</th>
              <th>Источник</th>
              <th onClick={() => toggleSort("count")}>Совпадений{arrow("count")}</th>
              <th onClick={() => toggleSort("max_score")}>Риск{arrow("max_score")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty">
                  Нет medium/high — после живого скана появятся здесь.
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr
                  key={`${r.host}-${r.source}`}
                  className={r.host ? "row-link" : ""}
                  onClick={() => drill(r)}
                  title={r.host ? `Открыть упоминания ${r.host}` : undefined}
                >
                  <td>
                    {r.host || "—"}
                    {isNewHost(r) ? <span className="badge-new">новый хост</span> : null}
                  </td>
                  <td>{r.source}</td>
                  <td>{r.count}</td>
                  <td className={r.max_score >= 70 ? "risk-high" : ""}>
                    {r.max_score >= 70 ? "HIGH" : "MEDIUM"} · {r.max_score}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
